/**
 * AI CONTEXT
 * Purpose: modal form to append a new task to a selected student's tasks array.
 * Inputs: optional fixed studentId, student search options, task type/description.
 * Writes: student doc tasks[] update via save hook.
 * Used by: StudentTaskList and StudentTaskCard.
 * Refactor: consolidate form validation and error handling into reusable helper.
 */
import { PlusOutlined } from "@ant-design/icons"
import { App, Form, Select } from "antd"
import TextArea from "antd/es/input/TextArea"
import { arrayUnion } from "firebase/firestore"
import { useEffect, useMemo, useState } from "react"

import ButtonToggleModal from "#blocks/ButtonToggleModal"
import Flex from "#blocks/Flex"
import FormWithValues from "#components/FormWithValues"
import { useUserContext } from "#contexts/UserContextProvider"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import {
  getStudentTaskTypeLabel,
  STUDENT_TASK_TYPES,
  StudentTask,
  StudentTaskType,
} from "#customTypes/composedTypes/studentTaskTypes"
import { Uuid } from "#customTypes/globalTypes"
import {
  useGetAllStudents,
  useGetOneStudent,
} from "#hooks/jotai/useGetStudents"
import { useUpdateDoc } from "#hooks/jotai/useSaveDoc"
import useOptimizedFilter from "#hooks/newHooks/useOptimizedFilter"
import { useErrorModal } from "#hooks/useErrorModal"
import { getNow } from "#utils/dateHelpers"
import { filterFormOptions } from "#utils/formHelpers"
import { getStudentOptions } from "#utils/studentHelpers"
import { ErrorType } from "#utils/textFormatters"
import useIsSuperView from "#views/SuperView/useIsSuperView"

const AddStudentTaskForm = ({
  studentId,
  showLabel = true,
}: {
  studentId: Uuid | null
  showLabel?: boolean
}) => {
  const [form] = Form.useForm()

  const { updateDoc: updateStudent } = useUpdateDoc(DOC_TYPES.STUDENTS)
  const { currentUserId } = useUserContext()
  const allStudents = useGetAllStudents()
  const [error, setError] = useState<ErrorType>("")

  const openErrorModal = useErrorModal("viewing AddStudentTaskForm")

  useEffect(() => {
    if (error) {
      const context = `
AddStudentTaskForm
`
      openErrorModal(error, context)
    }
  }, [error, openErrorModal])

  const isSuperView = useIsSuperView()

  const student = useGetOneStudent(studentId ?? "")
  const allStudentOptions = useMemo(() => {
    if (student) return getStudentOptions([student])
    return getStudentOptions(allStudents)
  }, [allStudents, student])

  useEffect(() => {
    if (studentId) {
      form.resetFields()
    }
  }, [studentId, form])

  const { onSearch, filteredItems: filteredStudentOptions } =
    useOptimizedFilter(allStudentOptions)

  const { message } = App.useApp()

  const handleSubmit = async (values: {
    studentId: string
    label: string
    type: StudentTaskType
  }) => {
    const selectedStudent =
      student ?? allStudents.find(({ id }) => id === values.studentId)

    const newTask: StudentTask = {
      id: Date.now().toString(),
      label: values.label,
      type: values.type,
      isCompleted: false,
      createdAt: getNow().toISOString(),
      createdBy: currentUserId!,
      completedAt: null,
      completedBy: null,
    }

    // arrayUnion appends server-side, so tasks added or completed since the
    // subscription last synced are kept.
    const savedId = await updateStudent(values.studentId, {
      tasks: arrayUnion(newTask),
    })
    if (savedId == null) return

    message.success(`Task added for ${selectedStudent?.name}.`)
    form.resetFields()
  }

  const handleOk = async () => {
    try {
      await form.validateFields()
      const values = form.getFieldsValue()
      await handleSubmit(values)
    } catch (error) {
      setError(error as ErrorType)
      throw error // Re-throw to prevent modal from closing
    }
  }

  return (
    <ButtonToggleModal
      buttonLabel={showLabel ? "+ Add New Task" : <PlusOutlined />}
      tooltip={!showLabel ? "Add New Task" : undefined}
      modalTitle="Add New Task"
      onOk={handleOk}
      size={isSuperView ? "small" : undefined}
    >
      <Flex col gap={10} style={{ padding: "10px 0" }}>
        <FormWithValues
          form={form}
          name="Add New Task"
          layout="vertical"
          initialValues={{
            type: STUDENT_TASK_TYPES.PARENT,
            studentId: studentId ?? undefined,
          }}
        >
          <Flex style={{ gap: 10, alignItems: "center" }}>
            <Form.Item name="studentId" label="Student">
              <Select
                disabled={!!studentId}
                style={{ width: 300 }}
                showSearch
                placeholder="Select student"
                options={filteredStudentOptions}
                onSearch={onSearch}
                filterOption={() => true}
                allowClear
              />
            </Form.Item>
          </Flex>

          <Form.Item name="label" label="Description">
            <TextArea placeholder="Task description" rows={3} />
          </Form.Item>
          <Form.Item name="type" label="Type">
            <Select
              showSearch
              placeholder="Select type"
              filterOption={filterFormOptions}
              options={Object.values(STUDENT_TASK_TYPES).map((type) => ({
                key: type,
                label: getStudentTaskTypeLabel(type),
                value: type,
              }))}
            />
          </Form.Item>
        </FormWithValues>
      </Flex>
    </ButtonToggleModal>
  )
}

export default AddStudentTaskForm
